import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Stack,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FitScreen as FitScreenIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
} from '@mui/icons-material';
import { PedigreeResponse } from '../../types';
import littersService from '@services/littersService';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

interface PedigreeModalProps {
  open: boolean;
  onClose: () => void;
  animalId: string;
  animalName?: string;
}

const PedigreeModal: React.FC<PedigreeModalProps> = ({
  open,
  onClose,
  animalId,
  animalName,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pedigree, setPedigree] = useState<PedigreeResponse | null>(null);
  const [upGenerations, setUpGenerations] = useState(3);
  const [downGenerations, setDownGenerations] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [showConfirmedOnly, setShowConfirmedOnly] = useState(false);
  const pedigreeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      loadPedigree();
    }
  }, [open, animalId, upGenerations, downGenerations]);

  const loadPedigree = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await littersService.getAnimalPedigree(animalId, upGenerations, downGenerations);
      setPedigree(data);
    } catch (err) {
      console.error('Error loading pedigree:', err);
      setError('Eroare la incarcarea pedigree-ului');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPNG = async () => {
    if (!pedigreeRef.current) return;

    try {
      const canvas = await html2canvas(pedigreeRef.current);
      const link = document.createElement('a');
      link.download = `pedigree-${animalName || animalId}-${Date.now()}.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch (err) {
      console.error('Error exporting PNG:', err);
      alert('Eroare la exportul PNG');
    }
  };

  const handleExportPDF = async () => {
    if (!pedigreeRef.current) return;

    try {
      const canvas = await html2canvas(pedigreeRef.current);
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'px',
        format: [canvas.width, canvas.height],
      });
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      pdf.save(`pedigree-${animalName || animalId}-${Date.now()}.pdf`);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      alert('Eroare la exportul PDF');
    }
  };

  const renderAnimalCard = (nodeId: string, isCenter: boolean = false) => {
    const node = pedigree?.nodes.find(n => n.id === nodeId);
    if (!node) return null;

    return (
      <Card
        sx={{
          minWidth: 150,
          bgcolor: isCenter ? 'primary.light' : 'background.paper',
          border: isCenter ? 2 : 1,
          borderColor: isCenter ? 'primary.main' : 'divider',
        }}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack spacing={0.5}>
            <Box display="flex" alignItems="center" gap={0.5}>
              {node.sex === 'M' && <MaleIcon fontSize="small" color="primary" />}
              {node.sex === 'F' && <FemaleIcon fontSize="small" color="secondary" />}
              <Typography variant="subtitle2" fontWeight="bold">
                {node.name}
              </Typography>
            </Box>
            {node.breed && (
              <Typography variant="caption" color="text.secondary">
                {node.breed}
              </Typography>
            )}
            {node.microchip && (
              <Typography variant="caption" color="text.secondary">
                CIP: {node.microchip}
              </Typography>
            )}
            {node.birthdate && (
              <Typography variant="caption" color="text.secondary">
                {new Date(node.birthdate).toLocaleDateString('ro-RO')}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const getParents = (nodeId: string) => {
    if (!pedigree) return { mother: null, father: null };
    
    const motherEdge = pedigree.edges.find(e => e.childId === nodeId && e.relation === 'mother');
    const fatherEdge = pedigree.edges.find(e => e.childId === nodeId && e.relation === 'father');
    
    return {
      mother: motherEdge ? motherEdge.parentId : null,
      father: fatherEdge ? fatherEdge.parentId : null,
    };
  };

  const renderGeneration = (nodeId: string, level: number, maxLevels: number) => {
    if (level > maxLevels) return null;

    const { mother, father } = getParents(nodeId);

    if (!mother && !father && level > 0) return null;

    return (
      <Box display="flex" flexDirection="column" gap={2}>
        {mother && (
          <Box>
            {renderAnimalCard(mother)}
            {level < maxLevels && (
              <Box ml={4} mt={2}>
                {renderGeneration(mother, level + 1, maxLevels)}
              </Box>
            )}
          </Box>
        )}
        {father && (
          <Box>
            {renderAnimalCard(father)}
            {level < maxLevels && (
              <Box ml={4} mt={2}>
                {renderGeneration(father, level + 1, maxLevels)}
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Pedigree {animalName ? `- ${animalName}` : ''}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Controls */}
        <Box mb={3}>
          <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Generatii Ascendente</InputLabel>
              <Select
                value={upGenerations}
                onChange={(e) => setUpGenerations(Number(e.target.value))}
                label="Generatii Ascendente"
              >
                <MenuItem value={2}>2</MenuItem>
                <MenuItem value={3}>3</MenuItem>
                <MenuItem value={4}>4</MenuItem>
                <MenuItem value={5}>5</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Generatii Descendente</InputLabel>
              <Select
                value={downGenerations}
                onChange={(e) => setDownGenerations(Number(e.target.value))}
                label="Generatii Descendente"
              >
                <MenuItem value={0}>0</MenuItem>
                <MenuItem value={1}>1</MenuItem>
                <MenuItem value={2}>2</MenuItem>
              </Select>
            </FormControl>

            <Box flex={1} />

            {/* Zoom controls */}
            <Tooltip title="Zoom Out">
              <IconButton
                size="small"
                onClick={() => setZoom(Math.max(50, zoom - 10))}
                disabled={zoom <= 50}
              >
                <ZoomOutIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="body2">{zoom}%</Typography>
            <Tooltip title="Zoom In">
              <IconButton
                size="small"
                onClick={() => setZoom(Math.min(200, zoom + 10))}
                disabled={zoom >= 200}
              >
                <ZoomInIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reset Zoom">
              <IconButton
                size="small"
                onClick={() => setZoom(100)}
              >
                <FitScreenIcon />
              </IconButton>
            </Tooltip>

            {/* Export buttons */}
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExportPNG}
              disabled={loading || !pedigree}
            >
              Export PNG
            </Button>
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExportPDF}
              disabled={loading || !pedigree}
            >
              Export PDF
            </Button>
          </Stack>
        </Box>

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        )}

        {/* Pedigree tree */}
        {!loading && pedigree && (
          <Box
            ref={pedigreeRef}
            sx={{
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top left',
              minHeight: 400,
              p: 3,
              bgcolor: 'background.default',
              borderRadius: 1,
              overflow: 'auto',
            }}
          >
            <Stack direction="row" spacing={4} alignItems="flex-start">
              {/* Animal central */}
              <Box>
                {renderAnimalCard(animalId, true)}
              </Box>

              {/* Ascendenti */}
              {upGenerations > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Parinti si Stramosi
                  </Typography>
                  {renderGeneration(animalId, 1, upGenerations)}
                </Box>
              )}
            </Stack>

            {/* Stats */}
            <Box mt={4}>
              <Stack direction="row" spacing={2}>
                <Chip
                  size="small"
                  label={`Total animale: ${pedigree.nodes.length}`}
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={`Relatii: ${pedigree.edges.length}`}
                  color="secondary"
                  variant="outlined"
                />
              </Stack>
            </Box>
          </Box>
        )}

        {/* Empty state */}
        {!loading && pedigree && pedigree.nodes.length === 1 && (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">
              Nu exista date de pedigree disponibile pentru acest animal.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Inchide</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PedigreeModal;
